import React, { useEffect, useState, useMemo, useRef } from "react";
import { APISD } from "../../../../../config/apisd";
import { APIMS } from "../../../../../config/apims";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import swal from "sweetalert";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserGraduate, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import PreviewFoto, { StudentAvatar, studentPhoto } from "./modal/preview-foto";
import ModalCetak from "./modal/cetak";

const safeStr = (v) => v == null ? "" : String(v);
const collator = new Intl.Collator("id", { numeric: true, sensitivity: "base" });
const toTitleCase = (v) => safeStr(v).toLowerCase().trim().replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

export default function Rapor() {
  document.title = "Rapor";
  const navigate = useNavigate();
  const location = useLocation();
  const [previewStudent, setPreviewStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [semesterId, setSemesterId] = useState("");
  const [semesterRows, setSemesterRows] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const selectAllRef = useRef(null);

  const [modalCetak, setModalCetak] = useState(false);

  useEffect(() => {
    const level = localStorage.getItem("level");
    if (level === "developer" || level === "superadmin") return;
    const permissions = JSON.parse(localStorage.getItem("role_permission") || "[]");
    if (!permissions.some((item) => item.menu?.url === location.pathname)) navigate("/dashboard");
  }, [location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `${token}`, "Content-Type": "application/json" } };
        const teacherId = localStorage.getItem("teacherID");
        const branchId = localStorage.getItem("ysb_branch_id") || localStorage.getItem("branch_code");
        let schoolId = localStorage.getItem("ysb_school_id") || localStorage.getItem("school_code");
        if (!teacherId) throw new Error("teacherID tidak ditemukan di localStorage. Silakan login ulang.");
        if (!branchId) throw new Error("Kode cabang tidak ditemukan di localStorage.");
        if (!schoolId) {
          // Akun dengan akses hanya satu sekolah dapat dikenali otomatis.
          const level = localStorage.getItem("level") || "";
          const schoolsRes = await APIMS.get(`/api/access/schools?page=1&limit=1000&ascending=1&branch=${encodeURIComponent(branchId)}&level=${encodeURIComponent(level)}`, config);
          const availableSchools = (schoolsRes?.data?.data || []).filter((school) => String(school.ysb_branch_id) === String(branchId));
          if (availableSchools.length === 1) schoolId = availableSchools[0].school_code;
          else throw new Error("Sekolah guru belum bisa ditentukan otomatis. Simpan ysb_school_id (kode sekolah) saat login.");
        }

        // Ambil tahun ajaran dan semester aktif otomatis, tanpa pilihan filter.
        const semesterRes = await APIMS.get("/api/access/semesters?page=1&limit=1000&ascending=0&search=", config);
        const allSemesters = semesterRes?.data?.data || [];
        const active = allSemesters.find((item) => Number(item.status) === 1);
        if (!active) throw new Error("Semester aktif tidak ditemukan.");
        const academicYearId = active.ysb_academic_year_id;
        if (!academicYearId) throw new Error("Tahun ajaran semester aktif tidak ditemukan.");
        const relevant = allSemesters.filter((item) => String(item.ysb_academic_year_id) === String(academicYearId))
          .sort((a, b) => Number(a.semester) - Number(b.semester));

        // Setiap semester satu POST, lalu hasilnya di-cache pada state.
        // Pindah tab semester tidak membuat request ulang.
        const pairs = await Promise.all(relevant.map(async (item) => {
          const payload = {
            ysb_academic_year_id: academicYearId,
            ysb_semester_id: item.id,
            ysb_school_id: schoolId,
            ysb_branch_id: branchId,
            ysb_teacher_id: teacherId,
          };
          try {
            const response = await APISD.post("/api/rapot-siakad-teacher", payload, config);
            const rows = response?.data?.data;
            return [String(item.id), Array.isArray(rows) ? rows : []];
          } catch (error) {
            // Belum ada assignment untuk semester ini bukan kegagalan seluruh halaman.
            const status = error?.response?.status;
            const message = String(error?.response?.data?.message || "").toLowerCase();
            if (status === 403 && message.includes("tidak terdapat data assignment")) {
              return [String(item.id), []];
            }
            throw error; // Error lain tetap ditampilkan agar tidak tersembunyi.
          }
        }));
        if (cancelled) return;
        setSemesterOptions(relevant);
        const rowsBySemester = Object.fromEntries(pairs);
        setSemesterRows(rowsBySemester);
        // Jika semester aktif belum memiliki data, tampilkan semester yang sudah ada siswa.
        const initialSemester = (rowsBySemester[String(active.id)] || []).length > 0
          ? active
          : relevant.find((item) => (rowsBySemester[String(item.id)] || []).length > 0) || active;
        setSemesterId(String(initialSemester.id));
        setLoaded(true);
      } catch (error) {
        if (cancelled) return;
        setLoaded(false);
        setSemesterRows({});
        swal({ title: "Gagal", text: error?.response?.data?.message || error.message || "Gagal mengambil data rapor", icon: "error" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const selectedSemester = semesterOptions.find((item) => String(item.id) === String(semesterId));
  const filteredData = useMemo(() => {
    const rows = (semesterRows[semesterId] || []).map((student, index) => ({
      ...student,
      _selectionKey: `${safeStr(student.ysb_student_id || student.id || student.name_student || student.nama_siswa)}-${index}`,
    }));
    const q = searchKeyword.trim().toLowerCase();
    return rows.filter((row) => safeStr(row.name_student || row.nama_siswa).toLowerCase().includes(q))
      .sort((a, b) => collator.compare(safeStr(a.name_student || a.nama_siswa), safeStr(b.name_student || b.nama_siswa)));
  }, [semesterRows, semesterId, searchKeyword]);

  const visibleKeys = filteredData.map((student) => student._selectionKey);
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((key) => selectedStudents.includes(key));
  const someVisibleSelected = visibleKeys.some((key) => selectedStudents.includes(key));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleStudent = (key) => {
    setSelectedStudents((current) => current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key]);
  };

  const toggleAll = (checked) => {
    setSelectedStudents((current) => checked
      ? Array.from(new Set([...current, ...visibleKeys]))
      : current.filter((key) => !visibleKeys.includes(key)));
  };

  return (
    <>
      <style>{`
        .rapor-select-checkbox {
          width: 17px;
          height: 17px;
          cursor: pointer;
          vertical-align: middle;
        }
        .rapor-bulk-action {
          border: 1px solid #EAB308;
          border-radius: 8px;
          padding: 8px 13px;
          background: #FFD600;
          color: #1F2937;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
        }
        .rapor-bulk-action:hover { background: #FACC15; }
        .rapor-tab-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          margin-left: auto;
        }
        .rapor-print-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          flex-shrink: 0;
          white-space: nowrap;
          border: 1px solid var(--rapor-border) !important;
          background: var(--rapor-bg) !important;
          color: var(--rapor-text) !important;
          border-radius: 7px;
          padding: 8px 11px;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.3;
          cursor: pointer;
          box-shadow: 0 2px 5px rgba(20, 37, 76, 0.07);
          transition: background-color .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .rapor-print-button:hover {
          background: var(--rapor-hover) !important;
          box-shadow: 0 4px 10px rgba(20, 37, 76, 0.13);
          transform: translateY(-2px);
        }
        .rapor-print-button:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(20, 37, 76, 0.1);
        }
        .rapor-print-button:focus-visible {
          outline: 2px solid var(--rapor-text);
          outline-offset: 2px;
        }
      `}</style>

      {/* {modalCetak && (
        <ModalCetak
          GetResponseData={GetResponseData}
          show={modalCetak}
          onHide={() => setModalCetak(false)}
        />
      )} */}

      {previewStudent && (
        <PreviewFoto
          student={previewStudent}
          show={true}
          onHide={() => setPreviewStudent(null)}
        />
      )}
      <div className="body sims-page">
        <div className="page-header-card">
          <div className="page-header-top d-flex">
            <div className="page-title-wrap">
              <span className="page-title-icon"><FontAwesomeIcon icon={faUserGraduate} /></span>
              <h4 className="page-title-text">Rapor</h4>
            </div>
          </div>
        </div>
        <div className="body-content sims-content">
          {loading && <div className="sims-loading"><PulseLoader color="#3f5bea" size={12} /></div>}
          {!loading && loaded && (
            <div className="sims-table-tab-card">
              <div className="sims-table-tab-head">
                <div className="sims-table-tab-title">
                  <h5>Daftar Rapor - Semester {selectedSemester?.semester || "-"}</h5>
                </div>
                <div className="rapor-tab-actions">
                {selectedStudents.length > 0 && (
                  <button
                    type="button"
                    className="rapor-bulk-action"
                    title="Pilihan aksi akan tersedia pada pembaruan selanjutnya"
                  >
                    Cetak Seluruh ({selectedStudents.length})
                  </button>
                )}
                <div className="sims-table-tab-semester-switch">
                  {semesterOptions.map((semester) => (
                    <button type="button" key={semester.id}
                      className={`sims-table-tab-semester ${String(semesterId) === String(semester.id) ? "is-active" : ""}`}
                      onClick={() => { setSemesterId(String(semester.id)); setSearchKeyword(""); setSelectedStudents([]); }}>
                      Semester {semester.semester}
                    </button>
                  ))}
                </div>
                </div>
              </div>
              <div className="sims-search-wrap mb-3 mt-3 px-2">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className="sims-search-icon"
                  />

                  <input
                    type="text"
                    className="sims-search-input"
                    placeholder="Cari nama siswa..."
                    value={searchKeyword}
                    onChange={(event) =>
                      setSearchKeyword(event.target.value)
                    }
                  />
                </div>

                <div className="sims-table-tab-wrap ">
                  <div className="sims-table-tab sims-table-scroll">
                     <table className="sims-table" style={{ minWidth: "900px" }}>
                       <thead>
                         <tr>
                           <th style={{ width: "48px", textAlign: "center" }}>
                             <input
                               ref={selectAllRef}
                               type="checkbox"
                               className="rapor-select-checkbox"
                               checked={allVisibleSelected}
                               disabled={filteredData.length === 0}
                               onChange={(event) => toggleAll(event.target.checked)}
                               aria-label="Pilih semua siswa yang ditampilkan"
                               title="Pilih semua siswa"
                             />
                           </th>
                           <th className="sims-col-number">No</th>
                           <th style={{ width: "100px" }}>Foto</th>
                           <th>Nama</th>
                           <th style={{ width: "550px", minWidth: "550px", textAlign: "center" }}>Aksi</th>
                         </tr>
                       </thead>
                       <tbody>
                         {filteredData.length === 0 ? (
                           <tr>
                             <td colSpan={5} className="sims-empty-state">
                               <div className="sims-empty-state-content">{(semesterRows[semesterId] || []).length === 0 ? "Data siswa rapor belum tersedia" : "Data rapor tidak ditemukan"}</div>
                             </td>
                           </tr>
                         ) : filteredData.map((student, index) => (
                           <tr key={student._selectionKey}>
                             <td style={{ textAlign: "center" }}>
                               <input
                                 type="checkbox"
                                 className="rapor-select-checkbox"
                                 checked={selectedStudents.includes(student._selectionKey)}
                                 onChange={() => toggleStudent(student._selectionKey)}
                                 aria-label={`Pilih ${student.name_student || student.nama_siswa || "siswa"}`}
                               />
                             </td>
                             <td className="sims-col-number">
                               <span className="sims-number-badge">{index + 1}</span>
                             </td>
                             <td>
                               <button
                                 type="button"
                                 className="sims-photo-button"
                                 title="Klik untuk melihat foto"
                                 aria-label={`Lihat foto ${student?.name_student || student?.nama_siswa || "siswa"}`}
                                 onClick={() => setPreviewStudent(student)}
                               >
                                 <StudentAvatar photo={studentPhoto(student)} />
                                 <span className="sims-photo-zoom" aria-hidden="true">
                                   <FontAwesomeIcon icon={faMagnifyingGlass} />
                                 </span>
                               </button>
                             </td>
                             <td>{toTitleCase(student?.name_student || student?.nama_siswa) || "-"}</td>
                             <td style={{ minWidth: "550px", whiteSpace: "nowrap" }}>
                               <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: "8px", flexWrap: "nowrap" }}>
                                 {[
                                   { label: "Cetak Halaman Depan", bg: "#DBEAFE", text: "#1D4ED8", border: "#93C5FD", hover: "#BFDBFE" },
                                   { label: "Cetak Tengah Semester", bg: "#EDE9FE", text: "#6D28D9", border: "#C4B5FD", hover: "#DDD6FE" },
                                   { label: "Cetak Akhir Semester", bg: "#DCFCE7", text: "#166534", border: "#86EFAC", hover: "#BBF7D0" },
                                 ].map(({ label, bg, text, border, hover }) => (
                                   <button
                                     key={label}
                                     type="button"
                                     className="rapor-print-button"
                                     style={{
                                       "--rapor-bg": bg,
                                       "--rapor-text": text,
                                       "--rapor-border": border,
                                       "--rapor-hover": hover,
                                       fontSize: "12px"
                                     }}
                                   >
                                     {/* <FontAwesomeIcon icon={faPrint} aria-hidden="true" /> */}
                                     <span>{label}</span>
                                   </button>
                                 ))}
                               </div>
                             </td>
                           </tr>
                         ))}
                       </tbody>
                     </table>
                   </div>
                 </div>

              </div>
          )}
        </div>
      </div>
    </>
  );
}
